// MaterialParamDiff — name-matched diff of a material instance's overridden
// parameters (scalar / vector / texture). Material instances store these as
// Scalar/Vector/TextureParameterValues arrays in their properties JSON; the
// raw JSON diff shows them index-keyed, so removing one mid-array reports as
// a cascade of "changed" rows. Matching by ParameterInfo.Name instead yields
// a clean added/removed/changed summary. Self-hides for assets with no such
// arrays (base UMaterials, non-materials).

import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { api } from '../../api.js';
import { NumStat } from './DeltaStat.js';

interface MaterialParamDiffProps {
  depotPath: string;
  leftRev: number;
  rightRev: number;
}

type ParamKind = 'scalar' | 'vector' | 'texture';
type ChangeKind = 'added' | 'removed' | 'changed';

interface ParamChange {
  kind: ParamKind;
  name: string;
  change: ChangeKind;
  left?: string | undefined;
  right?: string | undefined;
}

interface ParamCounts {
  scalar: number;
  vector: number;
  texture: number;
}

export function MaterialParamDiff({ depotPath, leftRev, rightRev }: MaterialParamDiffProps) {
  const leftQ = useQuery({
    queryKey: ['dam.preview.json', depotPath, leftRev],
    queryFn: () => fetchPreviewJson(depotPath, leftRev),
  });
  const rightQ = useQuery({
    queryKey: ['dam.preview.json', depotPath, rightRev],
    queryFn: () => fetchPreviewJson(depotPath, rightRev),
  });

  if (leftQ.isLoading || rightQ.isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Spinner />
        <span className="text-xs text-muted-foreground">Reading material parameters…</span>
      </div>
    );
  }

  const left = paramsOf(leftQ.data);
  const right = paramsOf(rightQ.data);
  if (!left && !right) return null; // not an overriding material instance

  const counts = (m: ParamMaps | null): ParamCounts => ({
    scalar: m ? m.scalar.size : 0,
    vector: m ? m.vector.size : 0,
    texture: m ? m.texture.size : 0,
  });
  const lc = counts(left);
  const rc = counts(right);
  const changes = diffParams(left, right);

  return (
    <div className="flex flex-col gap-1 rounded-md border border-neutral-800 bg-background/40 px-2.5 py-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Parameters
        </span>
        <NumStat label="Scalar" left={left ? lc.scalar : undefined} right={right ? rc.scalar : undefined} />
        <NumStat label="Vector" left={left ? lc.vector : undefined} right={right ? rc.vector : undefined} />
        <NumStat label="Texture" left={left ? lc.texture : undefined} right={right ? rc.texture : undefined} />
      </div>
      {changes.length === 0 ? (
        <span className="text-xs text-muted-foreground">No parameter overrides changed.</span>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {changes.map((c) => (
            <li key={`${c.kind}:${c.name}`} className="font-mono text-[11px]">
              <Badge tone={toneFor(c.change)}>{c.change}</Badge>{' '}
              <span className="text-muted-foreground">{c.kind}</span>{' '}
              <span className="text-foreground">{c.name}</span>
              {c.change === 'changed' && (
                <span className="text-muted-foreground">
                  : {c.left} → {c.right}
                </span>
              )}
              {c.change === 'removed' && c.left !== undefined && (
                <span className="text-muted-foreground"> (was {c.left})</span>
              )}
              {c.change === 'added' && c.right !== undefined && (
                <span className="text-muted-foreground"> ({c.right})</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function toneFor(c: ChangeKind): 'green' | 'red' | 'yellow' {
  return c === 'added' ? 'green' : c === 'removed' ? 'red' : 'yellow';
}

interface ParamMaps {
  scalar: Map<string, string>;
  vector: Map<string, string>;
  texture: Map<string, string>;
}

// Extract name→value maps for the three parameter categories. Returns null
// when none of the arrays exist (so the strip can self-hide).
function paramsOf(doc: unknown): ParamMaps | null {
  const exports = Array.isArray(doc) ? doc : null;
  if (!exports) return null;
  for (const e of exports) {
    if (typeof e !== 'object' || e === null) continue;
    const props = (e as Record<string, unknown>).Properties;
    if (typeof props !== 'object' || props === null) continue;
    const p = props as Record<string, unknown>;
    const hasAny =
      Array.isArray(p.ScalarParameterValues) ||
      Array.isArray(p.VectorParameterValues) ||
      Array.isArray(p.TextureParameterValues);
    if (!hasAny) continue;
    return {
      scalar: collect(p.ScalarParameterValues, scalarValue),
      vector: collect(p.VectorParameterValues, vectorValue),
      texture: collect(p.TextureParameterValues, textureValue),
    };
  }
  return null;
}

function collect(arr: unknown, valueOf: (v: unknown) => string): Map<string, string> {
  const m = new Map<string, string>();
  if (!Array.isArray(arr)) return m;
  for (const it of arr) {
    if (typeof it !== 'object' || it === null) continue;
    const rec = it as Record<string, unknown>;
    const info = rec.ParameterInfo;
    const name =
      typeof info === 'object' && info !== null
        ? strOr((info as Record<string, unknown>).Name)
        : undefined;
    if (!name) continue;
    m.set(name, valueOf(rec.ParameterValue));
  }
  return m;
}

function scalarValue(v: unknown): string {
  return typeof v === 'number' ? String(v) : JSON.stringify(v);
}

function vectorValue(v: unknown): string {
  if (typeof v === 'object' && v !== null) {
    const o = v as Record<string, unknown>;
    const fmt = (n: unknown) => (typeof n === 'number' ? n.toFixed(3) : '?');
    return `(${fmt(o.R)}, ${fmt(o.G)}, ${fmt(o.B)}, ${fmt(o.A)})`;
  }
  return JSON.stringify(v);
}

// Compare textures by ObjectName only — the ObjectPath carries import-index
// serialization noise (leading slash, trailing ".N") that differs between
// revisions without the referenced texture actually changing.
function textureValue(v: unknown): string {
  if (typeof v === 'object' && v !== null) {
    const name = strOr((v as Record<string, unknown>).ObjectName);
    if (name) return name.replace(/^[A-Za-z0-9]+'(.*)'$/, '$1');
  }
  return JSON.stringify(v);
}

function strOr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function diffParams(left: ParamMaps | null, right: ParamMaps | null): ParamChange[] {
  const out: ParamChange[] = [];
  for (const kind of ['scalar', 'vector', 'texture'] as const) {
    const l = left?.[kind] ?? new Map<string, string>();
    const r = right?.[kind] ?? new Map<string, string>();
    const names = new Set([...l.keys(), ...r.keys()]);
    for (const name of names) {
      const lv = l.get(name);
      const rv = r.get(name);
      if (lv !== undefined && rv === undefined) out.push({ kind, name, change: 'removed', left: lv });
      else if (lv === undefined && rv !== undefined) out.push({ kind, name, change: 'added', right: rv });
      else if (lv !== rv) out.push({ kind, name, change: 'changed', left: lv, right: rv });
    }
  }
  // Stable order: removed, added, changed — then by name within.
  const rank: Record<ChangeKind, number> = { removed: 0, added: 1, changed: 2 };
  return out.sort((a, b) => rank[a.change] - rank[b.change] || a.name.localeCompare(b.name));
}

async function fetchPreviewJson(depotPath: string, rev: number): Promise<unknown | null> {
  const res = await fetch(api.damPreviewUrl(depotPath, rev, 'json'), { credentials: 'include' });
  if (res.status !== 200) return null;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('json')) return null;
  const body = await res.json();
  if (typeof body === 'object' && body && 'noPreview' in body) return null;
  return body;
}
